// MaturaLogCoefSumTruthClosedTask.js
// Zadanie maturalne – zamknięte, typ P/F
// Własności: k·log_a b = log_a(b^k) oraz
//            m + log_a b = log_a(a^m b)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";
import { formatFractionLatex } from "../../../../utils/fractionLatex";

/* ── helpers ───────────────────────────────────────── */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// zawsze jawna podstawa logarytmu, brak log dziesiętnego
const logb = (a) => `\\log_{${a}}`;
const logExp = (a) => `\\log_{${a}}`;

// gcd + ładny zapis liczb (jak w innych zadaniach z logami)
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };
  let h0 = 0,
    k0 = 1,
    h1 = 1,
    k1 = 0,
    y = x;
  for (let i = 0; i < 20; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1;
    k0 = k1;
    h1 = h2;
    k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }
  let num = h1,
    den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0;
    den = k0;
  }
  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);
  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);

  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));
  return formatFractionLatex(num, den, { style: "inline" });
}

// zapis współczynnika k jako LaTeX (2, -2, 1/2, -1/2)
function coefLatex(num, den) {
  if (den === 1) {
    return String(num);
  }
  return formatFractionLatex(num, den, { style: "inline" });
}

/* ── generator ─────────────────────────────────────── */

// budujemy pierwsze zdanie: k·log_a u = log_a(vShown)
function buildFirstStatement(a) {
  // współczynnik k ∈ {2, -2, 1/2, -1/2}
  const coefOptions = [
    { num: 2, den: 1 },
    { num: -2, den: 1 },
    { num: 1, den: 2 },
    { num: -1, den: 2 },
  ];
  const { num, den } = pick(coefOptions);
  const k = num / den;

  // dla k całkowitych bierzemy u „zwykłe” (np. 2 lub 5),
  // dla k = ±1/2 bierzemy u = s^2, żeby było np. 25.
  let u, baseForRoot;
  if (den === 1) {
    baseForRoot = pick([2, 5]);
    u = baseForRoot;
  } else {
    baseForRoot = pick([2, 3, 5]);
    u = baseForRoot * baseForRoot;
  }

  const uLatex = niceLatexFromNumber(u);
  const kLatexStr = coefLatex(num, den);

  // poprawny argument po prawej: u^k
  const vTrue = Math.pow(u, k);
  const vTrueLatex = niceLatexFromNumber(vTrue);

  // tworzymy sensowny vFalse (inny, ale „podobny”)
  let candidates;
  if (den === 1) {
    // k = ±2
    const u2 = u * u;
    const u3 = u * u * u;
    const invU2 = 1 / (u * u);
    const invU = 1 / u;
    if (num > 0) {
      // dla 2·loga u: typowe błędy – log(u^3), log(1/u^2), log(1/u)
      candidates = [u3, invU2, invU];
    } else {
      // dla -2·loga u: błędy – log(u^2), log(1/u), log(1/u^3)
      candidates = [u2, invU, 1 / (u * u * u)];
    }
  } else {
    // k = ±1/2, u = s^2
    const s = baseForRoot; // pierwiastek z u
    const sq = u;
    const invS = 1 / s;
    const invSq = 1 / sq;
    if (num > 0) {
      // 1/2·log u – typowe błędy: log(1/s), log(1/u)
      candidates = [invS, invSq];
    } else {
      // -1/2·log u – błędy: log(s), log(1/u)
      candidates = [s, invSq];
    }
  }

  // wyrzucamy wszelkie, które przypadkiem dają poprawną wartość
  const filtered = candidates.filter(
    (val) => Math.abs(val - vTrue) > 1e-10
  );
  const vFalse =
    filtered.length > 0 ? pick(filtered) : vTrue + 1; // awaryjnie
  const vFalseLatex = niceLatexFromNumber(vFalse);

  const s1True = Math.random() < 0.5;
  const vShown = s1True ? vTrue : vFalse;
  const vShownLatex = s1True ? vTrueLatex : vFalseLatex;

  return {
    a,
    coefNum: num,
    coefDen: den,
    kLatex: kLatexStr,
    u,
    uLatex,
    vTrue,
    vTrueLatex,
    vShown,
    vShownLatex,
    s1True,
  };
}

// budujemy drugie zdanie: m + log_a b = log_a(wShown)
function buildSecondStatement(a) {
  const m = pick([1, 2, 3]);
  const b = pick([2, 5, 10]);

  const bLatex = niceLatexFromNumber(b);

  // poprawnie: m = log_a a^m, więc m + log_a b = log_a(a^m b)
  const wTrue = Math.pow(a, m) * b;
  const wTrueLatex = niceLatexFromNumber(wTrue);

  // fałszywie jak w CKE: m + log_a b = log_a(m·b)
  const wFalse = m * b;
  const wFalseLatex = niceLatexFromNumber(wFalse);

  const s2True = Math.random() < 0.5;
  const wShown = s2True ? wTrue : wFalse;
  const wShownLatex = s2True ? wTrueLatex : wFalseLatex;

  return {
    a,
    m,
    b,
    bLatex,
    wTrue,
    wTrueLatex,
    wShown,
    wShownLatex,
    s2True,
  };
}

function createJune2025OriginalTask() {
  return {
    type: "MaturaLogCoefSumTruthClosedTask",
    data: {
      a: 3,
      coefNum: 2,
      coefDen: 1,
      kLatex: "2",
      u: 5,
      uLatex: "5",
      vTrue: 25,
      vTrueLatex: "25",
      vShown: 25,
      vShownLatex: "25",
      s1True: true,
      m: 2,
      b: 5,
      bLatex: "5",
      wTrue: 45,
      wTrueLatex: "45",
      wShown: 10,
      wShownLatex: "10",
      s2True: false,
      originalCke: true,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 3,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // podstawa zawsze jawna i ≠ 10
  const a = pick([2, 3, 5]);
  const stmt1 = buildFirstStatement(a);
  const stmt2 = buildSecondStatement(a);

  return {
    type: "MaturaLogCoefSumTruthClosedTask",
    data: {
      a,
      ...stmt1,
      ...stmt2,
    },
  };
}

/* ── TREŚĆ ────────────────────────────────────────── */

export function renderTask({ data }) {
  const { a, kLatex, uLatex, vShownLatex, m, bLatex, wShownLatex } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Oceń prawdziwość poniższych stwierdzeń. Wybierz{" "}
        <InlineMath math="P" /> jeśli stwierdzenie jest prawdziwe,
        albo <InlineMath math="F" /> – jeśli jest fałszywe.
      </p>

      <div style={{ marginTop: 12 }}>
        <p>
          {!data.originalCke && <><strong>1.</strong>{" "}</>}
          Iloczyn{" "}
          <InlineMath
            math={`${kLatex}\\,${logb(a)}\\,${uLatex}`}
          />{" "}
          jest równy{" "}
          <InlineMath
            math={`${logb(a)}\\,${vShownLatex}`}
          />
          .
        </p>

        <p style={{ marginTop: 8 }}>
          {!data.originalCke && <><strong>2.</strong>{" "}</>}
          Suma{" "}
          <InlineMath math={String(m)} />{" "}
          <InlineMath math={`+\\,${logb(a)}\\,${bLatex}`} />{" "}
          jest równa{" "}
          <InlineMath
            math={`${logb(a)}\\,${wShownLatex}`}
          />
          .
        </p>
      </div>
    </div>
  );
}


/* ── INPUT (P/F) ───────────────────────────────────── */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, v) => {
    onChange?.({
      ...value,
      [key]: v,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={value.s1 || null}
          disabled={disabled}
          onChange={(v) => setValue("s1", v)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={value.s2 || null}
          disabled={disabled}
          onChange={(v) => setValue("s2", v)}
        />
      </div>
    </div>
  );
}

/* ── CHECK ────────────────────────────────────────── */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !["P","F"].includes(userAnswer.s1) || !["P","F"].includes(userAnswer.s2)) return false;
  const { s1True, s2True } = data;
  const { s1, s2 } = userAnswer;

  const s1Ok = (s1 === "P") === s1True;
  const s2Ok = (s2 === "P") === s2True;

  return s1Ok && s2Ok;
}

/* ── POPRAWNA ODPOWIEDŹ ───────────────────────────── */

export function renderAnswer({ data }) {
  const { s1True, s2True } = data;

  const s1Latex = `\\text{${s1True ? "P" : "F"}}`;
  const s2Latex = `\\text{${s2True ? "P" : "F"}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>
        <InlineMath math="1." /> <InlineMath math={s1Latex} />
      </p>
      <p>
        <InlineMath math="2." /> <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

/* ── ODPOWIEDŹ UŻYTKOWNIKA ────────────────────────── */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const s1 = userAnswer.s1 || "-";
  const s2 = userAnswer.s2 || "-";

  const s1Latex = s1 === "-" ? "-" : `\\text{${s1}}`;
  const s2Latex = s2 === "-" ? "-" : `\\text{${s2}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Twoja odpowiedź:</p>
      <p>
        1. <InlineMath math={s1Latex} />
      </p>
      <p>
        2. <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

/* ── WYJAŚNIENIE ───────────────────────────────────── */

export function renderExplanation({ data }) {
  const {
    a,
    coefNum,
    coefDen,
    kLatex,
    uLatex,
    vTrueLatex,
    vShownLatex,
    s1True,
    m,
    bLatex,
    wTrueLatex,
    wShownLatex,
    s2True,
  } = data;

  const logA = `\\log_{${a}}`;
  const compact = (latex) => String(latex).replace(/\\frac/g, "\\tfrac");

  const powerLatex = (u, num, den) => {
    const exp = formatFractionLatex(num, den, { style: "text" });
    return `${u}^{${exp}}`;
  };

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z własności logarytmów:
      </p>
      <BlockMath math={"k\\,\\log_{a} b = \\log_{a}(b^{k})"} />
      <BlockMath math={"m + \\log_{a} b = \\log_{a}(a^{m} b)"} />

      <p>
        <strong>Krok 2.</strong> Analizujemy pierwsze zdanie.
      </p>
      <p>
        Z lewej strony mamy{" "}
        <InlineMath math={`${kLatex}\\,${logA}\\,${uLatex}`} />, więc według
        własności:
      </p>
      <BlockMath
        math={`${compact(kLatex)}\\,${logA}\\,${compact(uLatex)} = ${logA}\\!\\left(${powerLatex(
          compact(uLatex),
          coefNum,
          coefDen
        )}\\right) = ${logA}\\,${compact(vTrueLatex)}`}
      />
      <p>
        W treści zadania po prawej stronie jest{" "}
        <InlineMath math={`${logA}\\,${vShownLatex}`} />. Porównujemy argumenty
        logarytmów:{" "}
        <InlineMath math={`${vTrueLatex} \\;\\text{ oraz }\\; ${vShownLatex}`} />.
        Dlatego zdanie <InlineMath math="1" /> jest{" "}
        <strong>{s1True ? "prawdziwe" : "fałszywe"}</strong>.
      </p>

      <p>
        <strong>Krok 3.</strong> Analizujemy drugie zdanie.
      </p>
      <p>
        Liczbę <InlineMath math={String(m)} /> możemy zapisać jako{" "}
        <InlineMath
          math={`${logA}\\,${niceLatexFromNumber(Math.pow(a, m))}`}
        />
        . Stąd:
      </p>
      <BlockMath
        math={`${m} + ${logA}\\,${compact(bLatex)} = ${logA}\\,${compact(niceLatexFromNumber(
          Math.pow(a, m)
        ))} + ${logA}\\,${compact(bLatex)} = ${logA}\\,${compact(wTrueLatex)}`}
      />
      <p>
        W treści zadania po prawej stronie jest{" "}
        <InlineMath math={`${logA}\\,${wShownLatex}`} />. Porównujemy argumenty
        logarytmów:{" "}
        <InlineMath math={`${wTrueLatex} \\;\\text{ oraz }\\; ${wShownLatex}`} />.
        Dlatego zdanie <InlineMath math="2" /> jest{" "}
        <strong>{s2True ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
    </div>
  );
}
